import React, { useMemo } from 'react';
import { Card, Empty, Typography } from 'antd';
import { Bar } from '@ant-design/charts';
import type { BqFtue } from '@/types';
import { dayLabel, fmtNum, fmtPct } from './bqMath';

const { Text } = Typography;

/** The 3 largest losses between consecutive steps (steps come most-reached first). */
function largestDrops(ftue: BqFtue) {
  const drops = ftue.steps.slice(1).map((s, i) => {
    const prev = ftue.steps[i];
    return {
      from: prev.step,
      to: s.step,
      lost: prev.users - s.users,
      share: (prev.users - s.users) / prev.users,
    };
  });
  return drops
    .filter((d) => d.lost > 0)
    .sort((a, b) => b.lost - a.lost)
    .slice(0, 3);
}

/**
 * Tutorial funnel of the latest install day. With a platform / version filter (`dayZero`) it is the day-0 funnel of
 * the FTUE & Progression page: steps reached within 24 h of install.
 */
export const BqFtueFunnel: React.FC<{ ftue: BqFtue | null; dayZero?: boolean }> = ({
  ftue,
  dayZero,
}) => {
  const data = useMemo(
    () =>
      (ftue?.steps ?? []).map((s) => ({
        step: s.step,
        users: s.users,
        share: ftue && ftue.cohortUsers > 0 ? s.users / ftue.cohortUsers : 0,
      })),
    [ftue]
  );
  if (!ftue) {
    return (
      <Card size="small" title="FTUE funnel">
        <Empty description="No install day in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }
  const drops = largestDrops(ftue);
  return (
    <Card
      size="small"
      title={`FTUE funnel · installs of ${dayLabel(ftue.installDate)} (${fmtNum(ftue.cohortUsers)} players)${dayZero ? ' · first 24 h' : ''}`}
    >
      <Bar
        data={data}
        xField="step"
        yField="users"
        height={Math.max(240, data.length * 18)}
        animate={false}
        axis={{ x: { title: false }, y: { title: false } }}
        tooltip={{
          items: [
            (d: { users: number; share: number }) => ({
              name: 'Players',
              value: `${fmtNum(d.users)} (${fmtPct(d.share)})`,
            }),
          ],
        }}
      />
      {drops.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ fontSize: 12 }}>
            Largest drops
          </Text>
          {drops.map((d) => (
            <div key={d.to} style={{ fontSize: 12 }}>
              <Text type="danger">−{fmtPct(d.share)}</Text>{' '}
              <Text type="secondary">
                {d.from} → {d.to} ({fmtNum(d.lost)} players)
              </Text>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
